import React, { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import { InputMask, InputMaskProps } from '@react-input/mask';
import { InputProps, TextInput } from './text-input/text-input';
import moment, { Moment } from 'moment';
import { IconButton, InputAdornment, Popover } from '@mui/material';
import { CalendarIcon } from '@phosphor-icons/react/dist/ssr/Calendar';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const ForwardedInputMask = forwardRef<HTMLInputElement, InputMaskProps>((props, forwardedRef) => (
  <InputMask ref={forwardedRef} mask="mm/dd/yyyy" replacement={{ d: /\d/, m: /\d/, y: /\d/ }} inputMode="numeric" {...props} />
));

export interface DateInputProps extends InputProps {
  selectAllOnFocus?: boolean,
  component?: React.FC<DateInputProps>,
  calendarIcon?: React.FC,
  calendarProps?: ComponentProps<typeof DateCalendar>,
  defaultOpen?: boolean,
}


export const DateInput = forwardRef((props: DateInputProps, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { selectAllOnFocus, component, calendarIcon, calendarProps,  ...rest } = props;

  const valid = typeof props.value !== 'string' || props.value.length < 10 ? false : moment(props.value, 'MM/DD/YYYY').isValid();

  const Component: any = component ? component : TextInput;

  const handleCalendarSelect = (value: string) => {
    props.onChange && props.onChange({target: { value, name: rest.name }} as any)
  };

  return <Component
    ref={ref}
    valid={valid}
    {...rest}
    InputProps={{
      ...props.InputProps,
      ref: inputRef,
      inputComponent: ForwardedInputMask,
      endAdornment: <InputAdornment position="end">
        <CalendarButton value={props.value as string || ''}
                        Icon={calendarIcon}
                        defaultOpen={props.defaultOpen}
                        calendarProps={calendarProps}
                        onChange={handleCalendarSelect} />
      </InputAdornment>,
      onFocus: (e: any) => {
        selectAllOnFocus && e.target.select();
        props.InputProps?.onFocus && props.InputProps.onFocus(e);
      }
    }}
  />;
});

type CalendarButtonProps = {
  value: string,
  onChange: (value: string) => void,
  Icon?: React.FC,
  calendarProps?: ComponentProps<typeof DateCalendar>,
  defaultOpen?: boolean,
};

const CalendarButton = ({ value, onChange, Icon = CalendarIcon, calendarProps = {}, defaultOpen }: CalendarButtonProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const ref = useRef<any>(null);

  const handleSelect = (val: Moment) => {
    setAnchorEl(null)
    onChange(val.format('MM/DD/YYYY'))
  }

  useEffect(() => {
    defaultOpen && setAnchorEl(ref.current);
  }, [defaultOpen]);

  return <>
    <IconButton ref={ref} size={'small'} sx={{ mr: -1 }} onClick={e => setAnchorEl(e.currentTarget)}>
      <Icon />
    </IconButton>
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      onClose={() => setAnchorEl(null)}
    >
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DateCalendar {...calendarProps}
                      value={value ? moment(value, 'MM/DD/YYYY') : null}
                      onChange={handleSelect} />
      </LocalizationProvider>
    </Popover>
  </>;
};
